import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import PageBanner from "../components/PageBanner";
import api from "../lib/api";
import SEO from "../lib/SEO";

export default function Shop() {
    const [cakes, setCakes] = useState([]);
    const [filter, setFilter] = useState("All");

    useEffect(() => {
        api.get("/cakes").then((r) => setCakes(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    }, []);

    const categories = useMemo(() => {
        const set = new Set(cakes.map((c) => c.category));
        return ["All", ...Array.from(set)];
    }, [cakes]);

    const filtered = filter === "All" ? cakes : cakes.filter((c) => c.category === filter);

    return (
        <main data-testid="shop-page">
            <SEO
                title="Shop — Cake Central"
                description="Browse our custom cake categories — wedding, birthday, baby shower, engagement, anniversary and more. Enquire for a bespoke quote."
                path="/shop"
            />
            <PageBanner
                eyebrow="Browse the studio"
                title="Shop our Cakes"
                image="https://images.unsplash.com/photo-1629576690723-bb3a22ae7126?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
            />

            <section className="bg-[#FFF7FB] py-10 md:py-14">
                <div className="px-4 sm:px-6 lg:px-10">
                    <p className="text-center text-ink-soft mb-8 max-w-2xl mx-auto leading-relaxed">
                        We don't sell online — every cake is bespoke. Pick the style closest to your vision and
                        send an enquiry, and we'll come back with a tailored quote.
                    </p>

                    <div className="flex flex-wrap gap-2 justify-center mb-10" data-testid="shop-filters">
                        {categories.map((c) => (
                            <button
                                key={c}
                                onClick={() => setFilter(c)}
                                className={`px-4 py-2 text-[10px] tracking-[0.25em] uppercase transition-colors ${
                                    filter === c
                                        ? "bg-blush text-white"
                                        : "bg-white border border-[#EAE3DE] text-ink hover:border-blush"
                                }`}
                                data-testid={`shop-filter-${c}`}
                            >
                                {c}
                            </button>
                        ))}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 md:gap-3 max-w-screen-2xl mx-auto">
                        {filtered.map((c, i) => (
                            <Link
                                key={c.id}
                                to={`/shop/${c.slug}`}
                                className="group block relative overflow-hidden card-lift"
                                data-testid={`shop-cake-${i}`}
                            >
                                <div className="aspect-[4/5] overflow-hidden">
                                    <img
                                        src={c.image_url}
                                        alt={c.name}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                        loading="lazy"
                                    />
                                </div>
                                <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent" />
                                <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                                    <p className="text-[10px] tracking-[0.3em] uppercase opacity-80 mb-1">{c.category}</p>
                                    <h3 className="font-display text-xl md:text-2xl mb-2">{c.name}</h3>
                                    <p className="text-xs text-white/85 line-clamp-2">{c.short_description}</p>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>
        </main>
    );
}
